import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('vwatch_token'));

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const saveToken = (t) => {
    localStorage.setItem('vwatch_token', t);
    setToken(t);
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
  };

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/auth/me`);
      setUser(res.data);
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  // Legacy login (backward compat)
  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    saveToken(res.data.access_token);
    await fetchUser();
    return res.data;
  };

  // Legacy register (backward compat)
  const register = async (email, password, name, role = 'BUYER') => {
    const res = await axios.post(`${API_URL}/api/auth/register`, { email, password, name, role });
    saveToken(res.data.access_token);
    await fetchUser();
    return res.data;
  };

  // New: Email start signup (sends OTP)
  const startEmailSignup = async (email, password, name, role) => {
    const res = await axios.post(`${API_URL}/api/auth/email/start-signup`, { email, password, name, role });
    return res.data;
  };

  // New: Verify OTP
  const verifyOtp = async (email, otp, context) => {
    const res = await axios.post(`${API_URL}/api/auth/email/verify-otp`, { email, otp, context });
    if (res.data.access_token) {
      saveToken(res.data.access_token);
      await fetchUser();
    }
    return res.data;
  };

  // New: Complete link (email to existing google account)
  const completeLink = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/email/complete-link`, { email, password });
    if (res.data.access_token) {
      saveToken(res.data.access_token);
      await fetchUser();
    }
    return res.data;
  };

  // New: Email login
  const emailLogin = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/email/login`, { email, password });
    saveToken(res.data.access_token);
    await fetchUser();
    return res.data;
  };

  // New: Forgot password start
  const forgotPasswordStart = async (email) => {
    const res = await axios.post(`${API_URL}/api/auth/email/forgot-password/start`, { email });
    return res.data;
  };

  // New: Forgot password verify + reset
  const forgotPasswordVerify = async (email, otp, newPassword) => {
    const res = await axios.post(`${API_URL}/api/auth/email/forgot-password/verify`, { email, otp, newPassword });
    return res.data;
  };

  // New: Google auth
  const googleAuth = async (idToken, intent = 'signin') => {
    const res = await axios.post(`${API_URL}/api/auth/google`, { idToken, intent });
    if (res.data.access_token) {
      saveToken(res.data.access_token);
      await fetchUser();
    }
    return res.data;
  };

  // New: Link Google to existing account
  const linkGoogle = async (idToken) => {
    const res = await axios.post(`${API_URL}/api/auth/link/google`, { idToken });
    if (res.data.access_token) {
      saveToken(res.data.access_token);
      await fetchUser();
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('vwatch_token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user, loading, login, register, logout, isAuthenticated: !!user,
    startEmailSignup, verifyOtp, completeLink, emailLogin,
    forgotPasswordStart, forgotPasswordVerify, googleAuth, linkGoogle, fetchUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
